"use client";

import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { ART, JIN_FACTS, STARTERS, type StarterId } from "./data";
import { c, useReducedMotion, useTypewriter } from "./hooks";

// The hero's handheld: Professor Jin (typed facts on a 7.6 s loop, held lines
// after A / while the journal is open), the starter screen that auto-cycles
// every 4.2 s until you touch the pad, D-pad / dots / A / B, and the two
// dialogs (journal notes, "Beta Portal") — all timings the prototype's.

export type HeroApi = { select: (id: StarterId) => void };

type Props = {
  current: number;
  setCurrent: (i: number) => void;
  apiRef: RefObject<HeroApi | null>;
  consoleRef: RefObject<HTMLDivElement | null>;
};

export default function HeroConsole({ current, setCurrent, apiRef, consoleRef }: Props) {
  const reduced = useReducedMotion();
  const starter = STARTERS[current];

  // --- Professor Jin -------------------------------------------------------
  const [line, setLine] = useState<{ text: string; key: number }>({ text: "", key: 0 });
  const lastFact = useRef(-1);
  const factTimer = useRef<number | null>(null);
  const resumeTimer = useRef<number | null>(null);
  const say = useCallback((text: string) => setLine((l) => ({ text, key: l.key + 1 })), []);
  const showRandomFact = useCallback(() => {
    let next: number;
    do next = Math.floor(Math.random() * JIN_FACTS.length); while (next === lastFact.current && JIN_FACTS.length > 1);
    lastFact.current = next;
    say(JIN_FACTS[next]);
  }, [say]);
  const clearProfessor = useCallback(() => {
    if (factTimer.current) window.clearInterval(factTimer.current);
    if (resumeTimer.current) window.clearTimeout(resumeTimer.current);
    factTimer.current = null; resumeTimer.current = null;
  }, []);
  const restartFacts = useCallback((showNow = true) => {
    clearProfessor();
    if (showNow) showRandomFact();
    factTimer.current = window.setInterval(showRandomFact, 7600);
  }, [clearProfessor, showRandomFact]);
  const holdProfessor = useCallback((text: string) => {
    clearProfessor();
    say(text);
    resumeTimer.current = window.setTimeout(() => restartFacts(), 7000);
  }, [clearProfessor, say, restartFacts]);
  useEffect(() => {
    const t = window.setTimeout(() => restartFacts(), 0);
    return () => { window.clearTimeout(t); clearProfessor(); };
  }, [restartFacts, clearProfessor]);
  const typed = useTypewriter(line.text, line.key, reduced);

  // --- Starter screen ------------------------------------------------------
  const [autoplay, setAutoplay] = useState(true);
  const [cycleKey, setCycleKey] = useState(0);
  const [spriteKey, setSpriteKey] = useState(0);
  const [screenLine, setScreenLine] = useState<string | null>(null);
  const currentRef = useRef(current);
  useEffect(() => { currentRef.current = current; }, [current]);
  const select = useCallback((id: StarterId) => {
    const i = STARTERS.findIndex((s) => s.id === id);
    if (i < 0) return;
    setCurrent(i);
    setScreenLine(null);
    setSpriteKey((k) => k + 1);
  }, [setCurrent]);
  const restart = useCallback(() => { setAutoplay(true); setCycleKey((k) => k + 1); }, []);
  useEffect(() => {
    if (!autoplay) return;
    const id = window.setInterval(() => select(STARTERS[(currentRef.current + 1) % STARTERS.length].id), 4200);
    return () => window.clearInterval(id);
  }, [autoplay, cycleKey, select]);
  useEffect(() => { apiRef.current = { select: (id) => { select(id); restart(); } }; }, [apiRef, select, restart]);

  const cycle = (step: number) => { select(STARTERS[(currentRef.current + step + STARTERS.length) % STARTERS.length].id); restart(); };
  const pressA = () => {
    setAutoplay(false);
    const s = STARTERS[currentRef.current];
    select(s.id);
    setScreenLine(`${s.name} IS YOUR FIRST PARTNER!`);
    holdProfessor(`Excellent choice. ${s.name} is ready to begin the Crimson journey with you.`);
  };

  // --- Journal dialog ------------------------------------------------------
  const notesRef = useRef<HTMLDialogElement>(null);
  const openNotes = () => {
    setAutoplay(false);
    clearProfessor();
    say("Journal open! These notes compare all three Crimson starters.");
    notesRef.current?.showModal();
  };
  const onNotesClose = () => { restart(); restartFacts(); };

  return (
    <div className={c("console-wrap")} aria-label="Professor Jin introduces the interactive starter preview" ref={consoleRef}>
      <div className={c("professor-intro")}>
        <div className={c("professor-visual")}>
          <img className={c("professor-portrait")} src={ART.professor} alt="Professor Jin in a white lab coat and round glasses" />
          <button className={c("journal-hotspot")} type="button" aria-label="Open Professor Jin's starter notes" onClick={openNotes} />
        </div>
        <div className={c("professor-dialogue", typed.typing && "typing")}>
          <strong className={c("professor-name")}>Professor Jin</strong>
          <p aria-live="polite" aria-label={line.text}>{typed.visible}</p>
        </div>
      </div>
      <div className={c("console-glow")} />
      <div className={c("console")}>
        <div className={c("screen-bezel")}>
          <div className={c("screen")}>
            <div className={c("screen-head", "pixel-font")}><span>Mojumon Crimson</span><span>Lv. 5</span></div>
            <div className={c("starter-stage")}>
              <img key={spriteKey} className={c("hero-sprite")} src={starter.src} alt={`16-bit ${starter.name} sprite`} />
            </div>
            <div className={c("screen-dialogue")}>{screenLine ?? `Will you choose ${starter.name}?`}</div>
            <div className={c("scanlines")} />
          </div>
        </div>
        <div className={c("console-controls")}>
          <div className={c("dpad")} aria-label="Browse starter choices">
            <button className={c("dpad-key", "dpad-up")} type="button" aria-label="Previous starter" onClick={() => cycle(-1)}>▲</button>
            <button className={c("dpad-key", "dpad-left")} type="button" aria-label="Previous starter" onClick={() => cycle(-1)}>◀</button>
            <button className={c("dpad-key", "dpad-right")} type="button" aria-label="Next starter" onClick={() => cycle(1)}>▶</button>
            <button className={c("dpad-key", "dpad-down")} type="button" aria-label="Next starter" onClick={() => cycle(1)}>▼</button>
          </div>
          <div className={c("starter-dots")} aria-label="Preview a starter">
            {STARTERS.map((s) => <button key={s.id} className={c("starter-dot", s.id === starter.id && "active")} data-id={s.id} aria-label={`Preview ${s.display}`} onClick={() => { select(s.id); restart(); }} />)}
          </div>
          <div className={c("ab")}>
            <button className={c("round")} type="button" aria-label="Open Professor Jin's journal" onClick={openNotes}>B</button>
            <button className={c("round")} type="button" aria-label="Choose the current starter" onClick={pressA}>A</button>
          </div>
        </div>
        <p className={c("controller-help")}>D-Pad: Browse • A: Choose • B: Journal</p>
      </div>

      <dialog ref={notesRef} className={c("modal", "notes-modal")} onClose={onNotesClose} onClick={(e) => { if (e.target === e.currentTarget) notesRef.current?.close(); }}>
        <div className={c("notes-box")}>
          <button className={c("notes-close")} type="button" aria-label="Close Professor Jin's notes" onClick={() => notesRef.current?.close()}>×</button>
          <h3>Choose Your Partner</h3>
          <p className={c("notes-lead")}>Professor Jin&apos;s field notes compare how each starter approaches the journey.</p>
          <div className={c("notes-grid")}>
            {STARTERS.map((s) => (
              <article key={s.id} className={c("note-card", s.id === starter.id && "active")}>
                <img src={s.src} alt={s.display} />
                <div><h4>{s.display}</h4><b>{s.type}</b><p>{s.note}</p></div>
              </article>))}
          </div>
        </div>
      </dialog>
    </div>
  );
}
